<template>
  <div class="articles-container">
    <h2 class="h3">
      P-Value Calculator
    </h2>
    <div id="heading1" class="article-section">
      <h3 class="h4">
        What Is P-Value in Statistics?
      </h3>
      <p>In statistics, the definition of p-value (also known as marginal significance level) is a measure that supports the alternative hypothesis and provides evidence against the null hypothesis. In other words, the p-value represents the smallest significance level at which the null hypothesis can be rejected. And the smaller the p-value, the stronger the evidence.</p>
      <p>The p-value in modern statistics was formally introduced by Karl Pearson in his Pearson’s chi-squared test, and later popularized by Ronald Fisher. You’ll learn how to find the p-value and interpret your results with this intuitive P-Value Calculator that returns code in Python and R.</p>
      <p>Keep in mind, this approach does not require setting a specific p-value significance level in advance. Instead, the researcher calculates the p-value, then compares it to a threshold value (such as 0.05 or 0.01) to determine whether the result is statistically significant. In the end, we reject the null hypothesis when the p-value is smaller than the specified threshold value.</p>
      <p>The p-value is an extremely powerful measure as it works for all distributions—no matter if we are dealing with the normal, Student’s T, binomial, or uniform distribution. Whatever the test, the p-value rationale holds. If the p-value is lower than the level of significance, you reject the null hypothesis.</p>
    </div>

    <div id="heading2" class="article-section">
      <h3 class="h4">
        How to Calculate the P-Value
      </h3>
      <p>The computation of p-values can be traced back to the 1700s when they were initially used to determine the human sex ratio at birth. The p-value in modern statistics was formally introduced by Karl Pearson in his Pearson’s chi-squared test, and later popularized by Ronald Fisher.</p>
      <p>To find the p-value, we first need a null hypothesis, a test statistic, and data. We also need to decide whether the test is one-tailed or two-tailed. Then, using the <a class="external-link" href="https://en.wikipedia.org/wiki/Cumulative_distribution_function" target="_blank">cumulative distribution function (CDF)</a>, we can determine the probability of the test statistic.</p>
      <p>Consider, for example, throwing dice. Given that we have even chances of getting either of the six sides of the die, the chance of getting a 1 is 1/6 the chance of getting a 2 is 1/6, the chance of getting a 3 is again 1/6, and of course, the chance of getting a 4 is 1/6 as well.</p>
      <p>The cumulative distribution function studies the chances that the random variable X is less than or equal to a particular value x, P(X ≤ x). We’ll go with the number 4. In other words, we want to find the probability of getting a number that is not higher than 4.</p>
      <p>How can we do that? In this case, this means adding 1/6 per each possible outcome up until 4. This is equal to 4/6.</p>
      <p>We now show you how to find the p-value with the test statistics being less than or equal to its value x in a given sample using the following formulas:</p>
      <p>For a one-sided left-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=cdf(x)\]
      </p>
      <p>For a one-sided right-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=1-\ cdf(x)\]
      </p>
      <p>For a two-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=2\ \times min\ \left\{cdf\ (x)\ ,\ 1-cdf\ (x)\right\}\]
      </p>
      <p>While computing the test statistic on the given data may be straightforward, determining the cumulative distribution function (CDF) can be challenging, especially for complex distributions.</p>
      <p>In the early 20th century, tables of values were used to extrapolate p-values from discrete values. Today, we have sophisticated statistical software. Fortunately, with the help of the P-Value Calculator, you can obtain the p-value easily.</p>
      <p>This is the most common way to do hypothesis testing. Instead of testing at preassigned levels of significance, we can find the smallest level of significance at which we can still reject the null hypothesis, given the observed sample statistic.</p>
    </div>

    <div id="heading3" class="article-section">
      <h3 class="h4">
        How to Find the P-Value Using the P-Value Calculator?
      </h3>
      <p>Our user-friendly tool eliminates the uncertainty about statistical significance when calculating the p-value and streamlines the entire process, empowering you to access the answers you need with ease.</p>
      <p>Here are the steps you need to follow to obtain the p-value of your sample with the 365 P-Value Calculator:</p>
      <ol class="ol">
        <li>Determine the distribution of the test statistic under the null hypothesis. For example, if you are conducting a t-test, the test statistic will follow a t-distribution with n-1 degrees of freedom under the null hypothesis.</li>
        <li>Input the computed test statistic value for your data sample.</li>
        <li>Determine the alternative hypothesis. Choose whether it is left-tailed, right-tailed, or two-tailed.</li>
        <li>If applicable, provide the degrees of freedom associated with the distribution of the test statistic.</li>
        <li>Specify the desired level of significance (alpha).</li>
        <li>Choose the number of digits after the decimal point to increase the precision of your results.</li>
      </ol>
      <p>The p-value provided by the calculator measures the strength of the evidence against the null hypothesis. It represents the probability of obtaining the observed data, assuming the null hypothesis is true.</p>
      <p>Based on the p-value, you can make decisions about the statistical significance of your results. If the p-value is below a predetermined significance level (e.g., 0.05), it suggests that the observed result is statistically significant and there is strong evidence to reject the null hypothesis. On the other hand, if the p-value is above the significance level, it indicates that the observed result is not statistically significant, and there is insufficient evidence to reject the null hypothesis.</p>
    </div>

    <div id="heading4" class="article-section">
      <h3 class="h4">
        How to Interpret the P-Value
      </h3>
      <p>In simple terms, a p-value is a probability measure that concludes whether an observed result occurred by chance as opposed to following a certain pattern.</p>
      <p>Please note that the p-value doesn’t confirm the null hypothesis as true or false. Nor does it provide any information about the size difference between the groups or variables being compared. The p-value in statistics simply indicates how strong the evidence against the null hypothesis is.</p>
      <p>Let’s give an example: as researchers, we want to establish whether two distinct populations are different. Our company has tasked us with checking what how our target audience responds to a new product. And more specifically, whether the male audience’s affinity is higher than the female’s.</p>
      <p>
        How to proceed in this case? Begin by determining the <a href="https://365datascience.com/tutorials/statistics-tutorials/null-hypothesis/" target="_blank">
          null hypothesis
        </a>. It postulates that the means of the populations are equal, or that the difference between the two means is zero.
      </p>
      <p>For the experiment, we conduct a survey and ask male and female respondents to express their affinity for this product on a scale from 1 to 5. We then calculate the mean (or average) affinity for both.</p>
      <p>If the men’s mean affinity is higher, you may be tempted to believe you proved your case. Unfortunately, this is not enough. Consider also showing that the difference between the two mean values follows a certain gender pattern, which is not due to pure chance.</p>
      <p>Therefore, the statistical test does not only compare the means for men and women but also explores how survey responses are shared out among both genders. The greater the dissimilarity between these patterns, the less likely it is that the difference occurred by chance. So, the statistical significance of the p-value reveals just that.</p>
      <p>For example, if the test produces a p-value of 0.0326, there is a 3.26% chance that the results happened randomly. As such probability levels are relatively small, we can conclude that the difference in affinity is probably not due to chance, but a result of the two groups’ distinctive opinions regarding the company’s new product.</p>
      <p>Had the p-value turned out to be 0.9429 instead, the results would have a 94.29% chance of being random. Such a high number practically excludes the possibility that the differences in affinity are due to gender.</p>
      <p>You can use a p-value calculator like ours to determine the statistical significance, but keep in mind what we wrote earlier:</p>
      <p class="bigger-colored-text">
        The smaller the p-value, the stronger the evidence that you should reject the null hypothesis.
      </p>
      <p>Therefore, when you see a report with the results of statistical tests, it’s important to also know how to interpret the p-value accurately. Normally, the closer to 0 it is, the better—depending on the hypotheses stated in that report, of course.</p>
      <h4 class="h5 h5-heading">
        What Is a Good P-Value?
      </h4>
      <p>Now that we know what kind of results we’re aiming for, it’s natural to ask how small should the p-value be exactly. What is the most appropriate percentage? Well, researchers set a cut-off value (or number) during their tests, known as the significance level.</p>
      <p>
        We state the <a href="https://365datascience.com/tutorials/statistics-tutorials/significance-level-reject-region/" target="_blank">
          significance level
        </a> in advance to determine what the size of the p-value must be in order to reject the null hypothesis. Typically, the researcher sets it arbitrarily before running the statistical test or experiment.
      </p>
      <p class="bigger-colored-text">
        If the p-value falls below the set significance level, we consider the test result to be statistically significant.
      </p>
      <p>In practice, the significance level is commonly referred to as the <span class="bolded-texts">alpha</span>.</p>
      <p>Unlike the p-value, the alpha does not depend on the underlying hypotheses, nor is it derived from any observational data. Instead, the significance level is determined in the scientific domain of the research. In many disciplines, including market research, social sciences, or medicine, researchers choose alpha values of 0.05 or 0.01. In other fields of scientific research, such as manufacturing, genetics, or particle physics, you will come across much lower numbers.</p>
      <p>For instance, when the existence of the elementary particle <a class="external-link" href="https://home.cern/science/physics/higgs-boson" target="_blank">Higgs boson</a> was established in 2012, the CERN scientists used an alpha of about 1 in 3.5 million. Understandably, when it comes to the history and the future of the universe, we wouldn’t want to leave anything to chance.</p>
      <p>When interpreting the output of a statistical test, you need to check whether the p-value is below the significance level. If that is the case, you can reject the null hypothesis at the level alpha.</p>
      <p>So here is the decision rule:</p>
      <ul class="ul">
        <li>If the p-value is lower than the significance level, we reject the null hypothesis.</li>
        <li>If the p-value is higher than the significance level, we fail to reject the null hypothesis.</li>
      </ul>
      <p>For example, with a p-value of 0.0321 and an alpha of 0.05, the verdict on the p-value interpretation example we unfolded earlier could be formulated as follows:</p>
      <p class="bolded-texts">
        “Based on the results, we can reject the null hypothesis at the level of significance a = 0.05, because the p-value is smaller than 0.05. Therefore, we conclude that men’s affinity for the new product is higher than women's.”
      </p>
      <p>Conversely, if the p-value is higher than alpha, for example, 0.1474, the conclusion could read as:</p>
      <p class="bolded-texts">
        “Based on the results, we do not reject the null hypothesis at the level of significance a = 0.05 since the p-value is greater than 0.05. So, we conclude that men and women have a similar affinity for the new product.”
      </p>
      <p>Whether you’re calculating the p-value yourself, reading a statistical report (as a decision-maker or otherwise), you should always pay attention to the significance level—especially if the analyst claims that the results are statistically significant.</p>
      <p><span class="bolded-texts">As a key takeaway</span>, please remember that we should aim for low p-values when running statistical tests. So, make sure you’re paying close attention, regardless of whether you’re using a p-value calculator or not. If the measure is lower than the significance level alpha, then we can conclude that the results are strong enough to reject the old notion in favor of a new one—the alternative hypothesis.</p>
    </div>

    <div id="heading5" class="article-section">
      <h3 class="h4">
        Does the P-Value Depend on the Alternative Hypothesis?
      </h3>
      <p>Yes, the p-value depends on the alternative hypothesis—the statement you’re trying to prove or find evidence for. The three types of alternative hypotheses are left-tailed, right-tailed, or two-tailed.</p>
      <p>The p-value definition is the probability of observing a test statistic as extreme or more extreme than the one calculated from the sample data, assuming that the null hypothesis <span class="inline-formulas">\[H_0\]</span> is true.</p>
      <p>When we observe a test statistic <span class="bolded-texts">t</span> from an unknown distribution <span class="bolded-texts">T</span>, we can calculate the p-value, which represents the probability of observing a test statistic value at least as extreme as t, assuming the null hypothesis is true:</p>
      <p>For a one-sided left-tailed test:</p>
      <p class="formulas-overflow">
        \[p=Pr\left(T\le t\ \middle|{\ H}_0\right)\]
      </p>
      <p>For a one-sided right-tailed test:</p>
      <p class="formulas-overflow">
        \[p=Pr\left(T\geq t\ \middle|{\ H}_0\right)\]
      </p>
      <p>For a two-tailed test:</p>
      <p class="formulas-overflow">
        \[p=2\ min\ \left\{Pr\left(T\geq t\ \middle|{\ H}_0\right)\ ,\ Pr\left(T\le t\ \middle|{\ H}_0\right)\right\}\]
      </p>
      <p>Here is how the p-value is expressed graphically:</p>
      <img src="../../../../assets/images/calculators/p-value/p-value-01.webp" alt="Graphical expression of the p-value.">
      <p>The graph shows the probability density function (PDF) with the observed test statistic denoted as a vertical line. In turn, the p-value would be represented by the shaded area under the tail(s) of the distribution to the left or right of the observed test statistic, depending on the directionality of the alternative hypothesis.</p>
      <p>For a symmetrical distribution, the p-value is expressed in the following way:</p>
      <p class="image-headings">
        Right-tailed p-value
      </p>
      <img src="../../../../assets/images/calculators/p-value/p-value-02.webp" alt="Right-tailed p-value graph.">
      <p class="image-headings">
        Left-tailed p-value
      </p>
      <img src="../../../../assets/images/calculators/p-value/p-value-03.webp" alt="Left-tailed p-value graph.">
      <p class="image-headings">
        Two-tailed p-value
      </p>
      <img src="../../../../assets/images/calculators/p-value/p-value-04.webp" alt="Two-tailed p-value graph.">
      <p>Meanwhile, this is the p-value in a non-symmetrical distribution:</p>
      <p class="image-headings img-top-margin-only">
        Right-tailed p-value
      </p>
      <img src="../../../../assets/images/calculators/p-value/p-value1-06.webp" alt="Right-tailed p-value graph.">
      <p class="image-headings">
        Left-tailed p-value
      </p>
      <img src="../../../../assets/images/calculators/p-value/p-value1-07.webp" alt="Left-tailed p-value graph.">
      <p class="image-headings two-tailed-graph">
        Two-tailed p-value
      </p>
      <img src="../../../../assets/images/calculators/p-value/p-value1-08.webp" alt="Two-tailed p-value graph.">
    </div>

    <div id="heading6" class="article-section">
      <h3 class="h4">
        How to Obtain the P-Value from a Z-Score
      </h3>
      <p>Before we dive into how to find the p-value from a z-score, let’s first define the term.</p>
      <p>A z-score is a standardized variable that indicates how far an observation is from the mean as a number of standard deviations. We calculate it by subtracting the mean from the data point and dividing the result by the standard deviation:</p>
      <p class="formulas-overflow">
        \[Z=\frac{X-\mu}{\sigma}\]
      </p>
      <p>Calculate the p-value from the z-score using the following formulas:</p>
      <p>For a left-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=\Phi(|z|)\]
      </p>
      <p>For a right-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=1\ -\ \Phi(|z|)\]
      </p>
      <p>For a two-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=2\ \times\ (1\ -\ \Phi(|z|))\]
      </p>
      <p>In these p-value from z-score formulas, Φ(z) is the CDF of the standard normal distribution and |z| is the absolute value of the z-statistic.</p>
      <p>
        The z-score is used when the test statistic follows the standard normal distribution N(with mean (μ) = 0 and standard deviation σ = 1). We can use it when we work with a <a href="https://365datascience.com/tutorials/statistics-tutorials/normal-distribution/" target="_blank">
          normal distribution
        </a> or when the sample size is sufficiently large.
      </p>
      <p>The standard normal distribution, also known as the z-distribution or the Gaussian distribution, has several key characteristics:</p>
      <ul class="ul">
        <li><span class="bolded-texts">Symmetry.</span> The standard normal distribution is symmetric around its mean, which is 0. This means that the curve is perfectly balanced on both sides.</li>
        <li><span class="bolded-texts">Bell-shaped curve.</span> The distribution’s curve resembles a bell, where the majority of the data points are clustered around the mean. The shape is determined by the standard deviation.</li>
        <li><span class="bolded-texts">Mean and standard deviation.</span> The mean of the standard normal distribution is 0, denoted as μ = 0. The standard deviation is σ = 1. This means that the values are expressed in terms of standard deviations from the mean.</li>
        <li><span class="bolded-texts">Under-the-curve area.</span> The total area under the standard normal distribution curve is equal to one. This represents the cumulative probability of all possible outcomes.</li>
        <li><span class="bolded-texts">Empirical rule.</span> The standard normal distribution follows the <a class="external-link" href="https://en.wikipedia.org/wiki/68%E2%80%9395%E2%80%9399.7_rule" target="_blank">empirical rule</a>, also known as the 68-95-99.7 rule. According to it, approximately 68% of the data falls within one standard deviation of the mean, while about 95% of the data falls within two standard deviations, and 99.7% of the data falls within three standard deviations.</li>
      </ul>
      <p class="image-headings">
        Standard normal distribution
      </p>
      <img src="../../../../assets/images/calculators/p-value/p-value1-09.webp" alt="Standard normal distribution graph.">
      <p>
        Thanks to the <a href="https://365datascience.com/tutorials/statistics-tutorials/central-limit-theorem/" target="_blank">
          central limit theorem
        </a>, the sampling distribution will approximate a normal distribution as the sample size increases. In general, a sample of at least 30 is often considered sufficient for the theorem to hold.
      </p>
      <p>
        The z-score is often used in hypothesis testing and <nuxt-link to="/confidence-interval-calculator/" target="_blank">
          calculating the confidence interval
        </nuxt-link> of the population mean. You can use our P-Value Calculator to find the p-value from the z-score of your experiment and interpret the results.
      </p>
    </div>

    <div id="heading7" class="article-section">
      <h3 class="h4">
        How to Obtain the P-Value from a T-Score
      </h3>
      <p>T-scores, also known as t-statistics, are used in statistical analysis when working with small sample sizes or when the population standard deviation is unknown. We use these values to assess the distinction between a sample mean and another sample mean or a theoretical value.</p>
      <p>
        Unlike z-scores, which are based on the standard normal distribution, t-scores are derived from the <a href="https://365datascience.com/tutorials/statistics-tutorials/students-t-distribution/" target="_blank">
          t-distribution
        </a>. The formula for estimating the t-statistic is equal to the following:
      </p>
      <p class="formulas-overflow">
        \[t_{n-1,\alpha}=\frac{\bar{x}-\mu}{s/\sqrt n}\]
      </p>
      <p>In this formula, we have:</p>
      <ul class="ul">
        <li><span class="inline-formulas">\[n\]</span> as the number of observations</li>
        <li><span class="inline-formulas">\[t_{n-1}\]</span> as the t-statistic with n − 1 degrees of freedom</li>
        <li><span class="inline-formulas">\[\bar{x}\]</span> as the sample mean</li>
        <li><span class="inline-formulas">\[\mu\]</span> as the population mean</li>
        <li><span class="inline-formulas">\[s\]</span> as the sample standard deviation</li>
      </ul>
      <p>The student’s t-distribution is one of the biggest breakthroughs in statistics as it allowed inference through small samples with an unknown population variance. This setting can be applied to many statistical problems we face today.</p>
      <p>The last characteristic of the t-score is that it contains degrees of freedom—the number of independent pieces of information available for estimating a population parameter. In this context, the degrees of freedom determine the shape of the distribution. As highlighted by the t-statistic formula, for a sample of n, we typically have n-1 degrees of freedom. What this means for a sample of 20 observations is that the degrees of freedom are 19.</p>
      <p class="image-headings">
        Student’s t-distribution
      </p>
      <img class="img-desktop" src="../../../../assets/images/calculators/p-value/p-value-10.webp" alt="Student's t-distribution graph.">
      <img class="img-mobile" src="../../../../assets/images/calculators/p-value/p-value-10-mobile.webp" alt="Student's t-distribution graph.">
      <p>You can calculate the p-value from t-score with our P-Value Calculator or using the following formulas:</p>
      <p>For a left-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value={\rm cdf}_{t,d}(t-score)\]
      </p>
      <p>For a right-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=1\ -\ {\rm cdf}_{t,d}(t-score)\]
      </p>
      <p>For a two-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=2\ \times\ (1\ -\ {\rm cdf}_{t,d}(\left|t-score\right|))\]
      </p>
      <p>In this formula, cdf(t-score) stands for the distribution’s cumulative distribution function and t-statistic with d degrees of freedom.</p>
    </div>

    <div id="heading8" class="article-section">
      <h3 class="h4">
        How to Obtain the P-Value from a Chi-Score
      </h3>
      <p>The chi-square test is used to examine the relationship between categorical variables in a dataset. By comparing observed and expected frequencies, this method helps us determine whether there is any significance—if the observed differences are due to chance or if there is a meaningful relationship between the variables. We calculate the chi-score in the following way:</p>
      <p class="formulas-overflow">
        \[\chi_c^2=\sum\frac{\left(O_i-E_i\right)^2}{E_i}\]
      </p>
      <p>And here is how to break down the formula:</p>
      <ul class="ul">
        <li><span class="inline-formulas">\[\chi_c^2\]</span> = chi-square test statistic</li>
        <li><span class="inline-formulas">\[O_i\]</span> = observed frequency</li>
        <li><span class="inline-formulas">\[E_i\]</span> = expected frequency</li>
      </ul>
      <p>Unlike the normal distribution and the t-distribution, the chi-square distribution is asymmetrical. As you’ll be able to see, its graph is highly skewed to the right.</p>
      <p class="image-headings">
        Chi Square Distribution
      </p>
      <img class="img-desktop" src="../../../../assets/images/calculators/p-value/p-value1-11.webp" alt="Chi Square Distribution graph.">
      <img class="img-mobile" src="../../../../assets/images/calculators/p-value/p-value1-11-mobile.webp" alt="Chi Square Distribution graph.">
      <p>Furthermore, the values on the X-axis start from 0, rather than from a negative number.</p>
      <p>Very few real-life events follow this type of distribution. In fact, the chi-square test is mostly used in hypothesis testing and computing confidence intervals. You’ll find it when determining the goodness of fit of categorical values.</p>
      <p>Find what the p-value in a chi-square is using the following formula or, alternatively utilize our P-Value Calculator:</p>
      <p>For a left-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value={\rm cdf}_{\chi^2,d}(\chi^2\ score)\]
      </p>
      <p>For a right-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=1\ -\ {\rm cdf}_{\chi^2,d}(\chi^2\ score)\]
      </p>
      <p>For a two-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=2\times min({\rm cdf}_{\chi^2,d}(\chi^2\ score),\ 1\ -\ {\rm cdf}_{\chi^2,d}(\chi^2\ score))\]
      </p>
      <p>In this chi-square p-value formula, we have:</p>
      <p><span class="inline-formulas">\[{cdf}_{\chi^2,d}\]</span> as the cumulative distribution function of the distribution of the distribution <span class="inline-formulas">\[\chi^2\]</span> with d degrees of freedom.</p>
    </div>

    <div id="heading9" class="article-section">
      <h3 class="h4">
        How to Find the P-Value Using the F-Statistic?
      </h3>
      <p>The F-test, also known as F-statistic, is the ratio of two variances. Named after Sir Ronald Fisher, who developed it back in 1920s, the measure is used in <a class="external-link" href="https://www.tibco.com/reference-center/what-is-analysis-of-variance-anova" target="_blank">analysis of variance (ANOVA)</a> and regression analysis to test the overall significance of a model or the equality of means when there are three or more groups.</p>
      <p>Let’s observe the formula for estimating the F-statistic when we have two samples, the first with <span class="inline-formulas">\[n_1\]</span> observations and the second with <span class="inline-formulas">\[n_2\]</span> observations:</p>
      <p class="formulas-overflow">
        \[F=\frac{s_1^2}{s_2^2}\]
      </p>
      <p>With the degrees of freedom in the nominator being <span class="inline-formulas">\[{df}_1=n_1-1\]</span>, and <span class="inline-formulas">\[{df}_2=n_2-1\]</span> in the denominator, respectively.</p>
      <p>Breaking down the formula, we have:</p>
      <ul class="ul">
        <li><span class="inline-formulas">\[s_1^2\]</span> as the first sample variance</li>
        <li><span class="inline-formulas">\[s_2^2\]</span> as the second sample variance</li>
      </ul>
      <p>The F-score follows the convention of choosing the larger ratio as the test statistic. This ensures that its value is always greater than or equal to 1.</p>
      <p>Similar to the chi-square, the F-distribution is asymmetrical and bounded from below by 0. We characterize it by two degrees of freedom: one for the numerator and another for the denominator. These values determine the shape and characteristics of the F-distribution for a specific statistical test.</p>
      <p class="image-headings">
        Probability Density Distribution of F
      </p>
      <img class="img-desktop" src="../../../../assets/images/calculators/p-value/p-value-12.webp" alt="Probability Density Distribution of F graph.">
      <img class="img-mobile" src="../../../../assets/images/calculators/p-value/p-value-12-mobile.webp" alt="Probability Density Distribution of F graph.">
      <p>You can calculate the p-value from the F-score using the following formula:</p>
      <p>For a left-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value={\rm cdf}_{f,d_1,d_2}(F-score)\]
      </p>
      <p>For a right-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=1\ -\ {\rm cdf}_{f,d_1,d_2}(F-score)\]
      </p>
      <p>For a two-tailed test:</p>
      <p class="formulas-overflow">
        \[p-value=2\times min({\rm cdf}_{f,d_1,d_2}(F-score),\ 1\ -\ {\rm cdf}_{f,d_1,d_2}(F-score))\]
      </p>
      <p>Alternatively, feel free to use our P-Value Calculator to atomize the process.</p>
      <p>By comparing the calculated F-statistic to critical values from the F-distribution, or by calculating the corresponding p-value, statisticians can determine the statistical significance of the model or the equality of means across groups.</p>
    </div>
  </div>
</template>

<script>
export default {
  mounted() {
    this.renderMathJax();
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.articles-container {
  .article-section {
    p {
      margin: 0 0 var(--spacing-20);

      &:last-of-type {
        margin: 0;
      }

      .external-link {
        display: inline;
      }
    }

    .h5-heading {
      margin: var(--spacing-40) 0;
    }

    .colored-bolded-texts {
      font-size: 16px;
      font-weight: 600;
      color: var(--accent-shade-3);
    }

    .bigger-colored-text {
      font-size: 20px;
      margin: var(--spacing-20) 0 var(--spacing-40);
      text-align: center;
      font-weight: 600;
      color: var(--accent-shade-3);
    }

    .image-headings {
      font-weight: 600;
      font-size: 22px;
      margin: var(--spacing-40) 0 0;
      text-align: center;
      color: var(--accent-shade-3);

      & ~ * {
        margin-top: 0;
      }
    }

    .img-mobile {
      display: none;
    }

    .ol,
    .ul {
      margin: var(--spacing-20) 0 var(--spacing-40);
      padding-left: var(--spacing-24);
    }
  }
}

@include bp($bp-laptop) {
  .articles-container {
    .article-section {
      .h5-heading {
        font-size: 18px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .articles-container {
    .article-section {
      .img-mobile {
        display: inline;
      }

      .img-desktop {
        display: none;
      }

      .h5-heading {
        font-size: 18px;
        margin-bottom: var(--spacing-32);
      }

      .ol,
      .ul {
        margin: var(--spacing-12) 0 var(--spacing-32);
        padding-left: var(--spacing-24);
      }

      .bigger-colored-text {
        margin: var(--spacing-12) 0 var(--spacing-32);
      }

      .image-headings {
        font-size: 18px;
        margin-top: var(--spacing-32);

        & ~ * {
          margin: 0 0 var(--spacing-32);
        }
      }

      img:nth-of-type(1) {
        margin: var(--spacing-32) 0;
      }
    }
  }
}

@import '../../../../assets/sass/calculators/articles.scss';
</style>
